import React, { useCallback, useContext, useMemo, useState } from "react";
import { useTextDomainContext } from "@/app/providers";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { object, func, bool } from "prop-types";
import { KeyTextField } from "@/components";
import { useSnackbar } from "notistack";
import { useAddPartner, useUpdatePartner } from "@/hooks";
import { DropdownOption } from "@/types/Dropdown.types";

interface PartnerRow {
  id?: string | number;
  key?: string;
  name?: string;
  companyUrl?: string;
  appUrl?: string;
  status?: string;
  smtpHost?: string | null;
  smtpPort?: number | string | null;
  smtpUser?: string | null;
  smtpPassword?: string | null;
  smtpUseTls?: boolean | null;
  smtpUseSsl?: boolean | null;
  smtpFromEmail?: string | null;
  smtpFromName?: string | null;
}

interface PartnerFormProps {
  row?: PartnerRow;
  handleClose: () => void;
}

interface PartnerFormState {
  partnerId: string | number;
  inputName: string;
  inputKey: string;
  inputCompanyUrl: string;
  inputAppUrl: string;
  inputStatus: {
    label: string;
    value: string;
  };
  inputSmtpHost: string;
  inputSmtpPort: string;
  inputSmtpUser: string;
  inputSmtpPassword: string;
  inputSmtpUseTls: boolean;
  inputSmtpUseSsl: boolean;
  inputSmtpFromEmail: string;
  inputSmtpFromName: string;
}

interface AddPartnerProps {
  open?: boolean;
  handleClose: () => void;
  row?: PartnerRow;
}

// Child component keeps local form state and is keyed by the incoming row id.
// When `row` changes, the key changes and the component remounts, creating fresh state
// derived from props without calling setState inside an effect.
const PartnerForm = ({
  row = {},
  handleClose: parentClose,
}: PartnerFormProps) => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const { enqueueSnackbar } = useSnackbar();

  const initialValue = useMemo<PartnerFormState>(
    () => ({
      partnerId: "",
      inputName: "",
      inputKey: "",
      inputCompanyUrl: "",
      inputAppUrl: "",
      inputStatus: {
        label: gettext("Active"),
        value: "Active",
      },
      inputSmtpHost: "",
      inputSmtpPort: "",
      inputSmtpUser: "",
      inputSmtpPassword: "",
      inputSmtpUseTls: true,
      inputSmtpUseSsl: false,
      inputSmtpFromEmail: "",
      inputSmtpFromName: "",
    }),
    [gettext],
  );

  // Initialize form from row (if provided) or from initialValue. Because this component
  // is keyed by row.id in the parent, this initializer will run on remount when the row changes.
  const [form, setForm] = useState<PartnerFormState>(() => {
    if (row && (row.key || row.id)) {
      const rowStatus = row.status || initialValue.inputStatus.value;
      return {
        partnerId: row.id || initialValue.partnerId,
        inputKey: row.key || initialValue.inputKey,
        inputName: row.name || initialValue.inputName,
        inputCompanyUrl: row.companyUrl || initialValue.inputCompanyUrl,
        inputAppUrl: row.appUrl || initialValue.inputAppUrl,
        inputStatus: {
          label: rowStatus,
          value: rowStatus,
        },
        inputSmtpHost: row.smtpHost || "",
        inputSmtpPort:
          row.smtpPort !== undefined && row.smtpPort !== null
            ? String(row.smtpPort)
            : "",
        inputSmtpUser: row.smtpUser || "",
        inputSmtpPassword: row.smtpPassword || "",
        inputSmtpUseTls:
          row.smtpUseTls !== undefined && row.smtpUseTls !== null
            ? !!row.smtpUseTls
            : true,
        inputSmtpUseSsl:
          row.smtpUseSsl !== undefined && row.smtpUseSsl !== null
            ? !!row.smtpUseSsl
            : false,
        inputSmtpFromEmail: row.smtpFromEmail || "",
        inputSmtpFromName: row.smtpFromName || "",
      };
    }
    return initialValue;
  });

  const resetForm = useCallback(() => setForm(initialValue), [initialValue]);

  const { addPartner, loading: adding } = useAddPartner(
    () => {
      resetForm();
      parentClose();
      enqueueSnackbar(gettext("The new partner has been added."), {
        variant: "success",
      });
    },
    (error) => {
      if (error === "409") {
        // handle conflict
      } else if (error === "410") {
        enqueueSnackbar(
          gettext("You are not authorized to perform this action"),
          { variant: "error" },
        );
      } else {
        enqueueSnackbar(gettext("Error: Something went wrong!"), {
          variant: "error",
        });
      }
    },
  );

  const { updatePartner, updating } = useUpdatePartner(
    () => {
      resetForm();
      parentClose();
      enqueueSnackbar(gettext("The partner has been updated."), {
        variant: "success",
      });
    },
    (error) => {
      if (error === "409") {
        // handle conflict
      } else if (error === "410") {
        enqueueSnackbar(
          gettext("You are not authorized to perform this action"),
          { variant: "error" },
        );
      } else {
        enqueueSnackbar(`Backend Error: ${error}`, { variant: "error" });
      }
    },
  );

  const submitHandler = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const parsedPort = parseInt(form.inputSmtpPort, 10);
    const variables: any = {
      name: form.inputName,
      key: form.inputKey,
      companyUrl: form.inputCompanyUrl,
      appUrl: form.inputAppUrl,
      status: form.inputStatus.value,
      smtpHost: form.inputSmtpHost.trim() || null,
      smtpPort: !isNaN(parsedPort) ? parsedPort : null,
      smtpUser: form.inputSmtpUser.trim() || null,
      smtpPassword: form.inputSmtpPassword || null,
      smtpUseTls: form.inputSmtpUseTls,
      smtpUseSsl: form.inputSmtpUseSsl,
      smtpFromEmail: form.inputSmtpFromEmail.trim() || null,
      smtpFromName: form.inputSmtpFromName.trim() || null,
    };

    if (row?.id) {
      variables.partnerId = row.id;
      updatePartner({ variables, refetchQueries: ["GetPartners"] }).then();
    } else {
      addPartner({ variables, refetchQueries: ["GetPartners"] }).then();
    }
  };

  const handleKeyChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((p) => ({ ...p, inputKey: event.target.value }));
  const handleNameChange = (value: string) =>
    setForm((p) => ({ ...p, inputName: value }));
  const handleCompanyUrlChange = (value: string) =>
    setForm((p) => ({ ...p, inputCompanyUrl: value }));
  const handleAppUrlChange = (value: string) =>
    setForm((p) => ({ ...p, inputAppUrl: value }));

  return (
    <form onSubmit={submitHandler}>
      <Stack spacing={4} sx={{ height: "100%" }}>
        <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
          {!row?.id && gettext("Add New Partner")}
          {row?.id && gettext("Edit Partner")}
        </Typography>

        <Box sx={{ height: "100%" }}>
          <Grid container spacing={4} sx={{ width: "100%" }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                type="text"
                label={gettext("Name")}
                placeholder={gettext("Enter name")}
                size="small"
                required
                fullWidth
                value={form.inputName}
                onChange={(e) => handleNameChange(e.target.value)}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <KeyTextField
                type="text"
                label={gettext("Key")}
                placeholder={gettext("Key, Alpha numeric and dash only")}
                size="small"
                required
                fullWidth
                value={form.inputKey}
                onChange={handleKeyChange}
                disabled={!!row?.key}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                type="text"
                label={gettext("Company website")}
                placeholder={gettext("https://www.example.com")}
                size="small"
                required
                fullWidth
                autoComplete="off"
                value={form.inputCompanyUrl}
                onChange={(e) => handleCompanyUrlChange(e.target.value)}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                type="text"
                label={gettext("App URL")}
                placeholder={gettext("https://www.example.com")}
                size="small"
                required
                fullWidth
                autoComplete="off"
                value={form.inputAppUrl}
                onChange={(e) => handleAppUrlChange(e.target.value)}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Autocomplete
                disablePortal
                options={[
                  { label: gettext("Active"), value: "Active" },
                  {
                    label: gettext("InActive"),
                    value: "InActive",
                  },
                  { label: gettext("Deleted"), value: "Deleted" },
                  { label: gettext("Blocked"), value: "Blocked" },
                ]}
                value={form.inputStatus}
                onChange={(_event, newValue: DropdownOption | null) =>
                  setForm((p) => ({
                    ...p,
                    inputStatus: newValue
                      ? {
                          label: String(newValue.label),
                          value: String(newValue.value),
                        }
                      : p.inputStatus,
                  }))
                }
                size="small"
                fullWidth
                isOptionEqualToValue={(
                  option: DropdownOption,
                  value: DropdownOption,
                ) =>
                  String(option.value).toLowerCase() ===
                  String(value.value).toLowerCase()
                }
                renderInput={(params) => (
                  <TextField {...params} required label={gettext("Status")} />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }} />
              <Typography sx={{ fontSize: 16, fontWeight: 600, mt: 1 }}>
                {gettext("SMTP / Email Settings")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {gettext(
                  "Configure partner SMTP server and sender info. Leave empty to use system environment default.",
                )}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                type="email"
                label={gettext("Sender Email Address")}
                placeholder={gettext("noreply@partner.dk")}
                helperText={gettext("Can differ from SMTP username")}
                size="small"
                fullWidth
                autoComplete="off"
                value={form.inputSmtpFromEmail}
                onChange={(e) =>
                  setForm((p) => ({ ...p, inputSmtpFromEmail: e.target.value }))
                }
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                type="text"
                label={gettext("Sender Display Name")}
                placeholder={gettext("Partner Name")}
                size="small"
                fullWidth
                autoComplete="off"
                value={form.inputSmtpFromName}
                onChange={(e) =>
                  setForm((p) => ({ ...p, inputSmtpFromName: e.target.value }))
                }
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                type="text"
                label={gettext("SMTP Host")}
                placeholder={gettext("smtp.example.com")}
                size="small"
                fullWidth
                autoComplete="off"
                value={form.inputSmtpHost}
                onChange={(e) =>
                  setForm((p) => ({ ...p, inputSmtpHost: e.target.value }))
                }
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                type="number"
                label={gettext("SMTP Port")}
                placeholder={gettext("587")}
                size="small"
                fullWidth
                autoComplete="off"
                value={form.inputSmtpPort}
                onChange={(e) =>
                  setForm((p) => ({ ...p, inputSmtpPort: e.target.value }))
                }
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                type="text"
                label={gettext("SMTP Username")}
                placeholder={gettext("Username / API Key")}
                size="small"
                fullWidth
                autoComplete="off"
                value={form.inputSmtpUser}
                onChange={(e) =>
                  setForm((p) => ({ ...p, inputSmtpUser: e.target.value }))
                }
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                type="password"
                label={gettext("SMTP Password")}
                placeholder={gettext("Password")}
                size="small"
                fullWidth
                autoComplete="new-password"
                value={form.inputSmtpPassword}
                onChange={(e) =>
                  setForm((p) => ({ ...p, inputSmtpPassword: e.target.value }))
                }
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Stack direction="row" spacing={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.inputSmtpUseTls}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          inputSmtpUseTls: e.target.checked,
                          inputSmtpUseSsl: e.target.checked
                            ? false
                            : p.inputSmtpUseSsl,
                        }))
                      }
                    />
                  }
                  label={gettext("Use TLS")}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.inputSmtpUseSsl}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          inputSmtpUseSsl: e.target.checked,
                          inputSmtpUseTls: e.target.checked
                            ? false
                            : p.inputSmtpUseTls,
                        }))
                      }
                    />
                  }
                  label={gettext("Use SSL")}
                />
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <Paper elevation={0}>
          <Grid container spacing={4} sx={{ width: "100%" }}>
            <Grid size={{ xs: 6 }} sx={{ textAlign: "center" }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  resetForm();
                  parentClose();
                }}
              >
                {gettext("Cancel")}
              </Button>
            </Grid>

            <Grid size={{ xs: 6 }} sx={{ textAlign: "center" }}>
              <Button
                variant="contained"
                loading={adding || updating}
                fullWidth
                type="submit"
              >
                {gettext("Save")}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Stack>
    </form>
  );
};

PartnerForm.propTypes = {
  row: object,
  handleClose: func,
};

export const AddPartner = (props: AddPartnerProps) => {
  return (
    <Drawer
      anchor={"right"}
      open={props.open}
      onClose={props.handleClose}
      sx={{ zIndex: (theme) => theme.zIndex.appBar + 1 }}
      slotProps={{
        paper: {
          sx: {
            width: { xs: "100%", sm: 540 },
            maxWidth: "100%",
          },
        },
      }}
    >
      <Box sx={{ width: "auto", p: 4 }}>
        {/* Key the form by row id so it remounts and re-initializes state when row changes */}
        <PartnerForm
          key={props.row?.id ?? "new"}
          row={props.row}
          handleClose={props.handleClose}
        />
      </Box>
    </Drawer>
  );
};

AddPartner.propTypes = {
  open: bool,
  handleClose: func,
  row: object,
};
